import { 
  collection, 
  doc, 
  setDoc, 
  getDoc, 
  updateDoc, 
  increment, 
  serverTimestamp,
  Timestamp,
  query,
  where,
  getDocs,
  runTransaction
} from 'firebase/firestore';
import { funnelDb } from '../firebase';

// 퍼널 데이터 타입 정의 (QR 진입 건수만)
export interface FunnelData {
  date: string; // YYYY-MM-DD 형식
  qrEntries: number; // QR 인식 후 진입 건수
  lastUpdated: Timestamp;
}

// 체험 시간 데이터 타입 정의
export interface ExperienceTimeData {
  avatarRegistrationTime: Timestamp; // 화신 등록 시간
  messageSentTime: Timestamp; // 메시지 전송 시간
  experienceDuration: number; // 체험 시간 (밀리초)
  date: string; // YYYY-MM-DD 형식
  lastUpdated: Timestamp;
}

// 배후성 선택 데이터 타입 정의
export interface BackgroundSelectionData {
  date: string; // YYYY-MM-DD 형식
  selections: Record<string, number>; // 배후성 이름별 선택 횟수
  totalSelections: number; // 총 선택 횟수
  lastUpdated: Timestamp;
}

// 퀴즈 진행도 데이터 타입 정의 (간단 버전)
export interface QuizProgressData {
  date: string; // YYYY-MM-DD 형식
  quizzes: {
    quiz1: {
      entries: number; // 진입 수
      correct: number; // 정답 수
      incorrect: number; // 오답 수
    };
    quiz2: {
      entries: number;
      correct: number;
      incorrect: number;
    };
    quiz3: {
      entries: number;
      correct: number;
      incorrect: number;
    };
    quiz4: {
      entries: number;
      correct: number;
      incorrect: number;
    };
    quiz5: {
      entries: number;
      correct: number;
      incorrect: number;
    };
    quiz6: {
      entries: number;
      correct: number;
      incorrect: number;
    };
  };
  lastUpdated: Timestamp;
}

// 체험 중단 포인트 데이터 타입 정의
export interface DropoutData {
  date: string; // YYYY-MM-DD 형식
  registeredUsers: number; // 등록한 유저 수
  messageSentUsers: number; // 메시지를 보낸 유저 수
  endingReachedUsers: number; // 엔딩까지 도달한 유저 수
  dropoutRate: number; // 이탈율 (%)
  completionRate: number; // 체험 완료율 (%)
  lastUpdated: Timestamp;
}

// 메시지 전송 횟수 데이터 타입 정의
export interface MessageSendCountData {
  date: string; // YYYY-MM-DD 형식
  totalMessageSends: number; // 총 메시지 전송 횟수
  lastUpdated: Timestamp;
}


// 오늘 날짜를 YYYY-MM-DD 형식으로 반환
const getTodayString = (): string => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

// 퍼널 데이터 초기화 (QR 진입 건수만)
const initializeFunnelData = (date: string): FunnelData => {
  return {
    date,
    qrEntries: 0,
    lastUpdated: serverTimestamp() as Timestamp
  };
};

// QR 진입 건수만 업데이트 (동시성 문제 해결)
export const updateFunnelData = async (date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const preshowQRCollection = collection(funnelDb, 'preshow-qr');
      const docRef = doc(preshowQRCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 QR 진입 건수만 증가
          transaction.update(docRef, {
            qrEntries: increment(1),
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeFunnelData(targetDate);
          initialData.qrEntries = 1; // QR 진입 건수를 1로 설정
          transaction.set(docRef, initialData);
        }
      });
      
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`QR 진입 건수 업데이트 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('QR 진입 건수 업데이트 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// QR 인식 후 진입 추적 (프리쇼 QR)
export const trackQREntry = async (date?: string): Promise<void> => {
  await updateFunnelData(date);
};

// 별자리연회 QR 진입 건수 업데이트 (동시성 문제 해결)
export const updateConstellationQRData = async (date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const constellationQRCollection = collection(funnelDb, 'constellation-qr');
      const docRef = doc(constellationQRCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 QR 진입 건수만 증가
          transaction.update(docRef, {
            qrEntries: increment(1),
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeFunnelData(targetDate);
          initialData.qrEntries = 1; // QR 진입 건수를 1로 설정
          transaction.set(docRef, initialData);
        }
      });
      
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`별자리연회 QR 진입 건수 업데이트 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('별자리연회 QR 진입 건수 업데이트 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 별자리연회 QR 인식 후 진입 추적
export const trackConstellationQREntry = async (date?: string): Promise<void> => {
  await updateConstellationQRData(date);
};

// 특정 날짜의 퍼널 데이터 조회 (프리쇼 QR)
export const getFunnelData = async (date: string): Promise<FunnelData | null> => {
  try {
    const preshowQRCollection = collection(funnelDb, 'preshow-qr');
    const docRef = doc(preshowQRCollection, date);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as FunnelData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('퍼널 데이터 조회 실패:', error);
    return null;
  }
};

// 특정 날짜의 별자리연회 QR 데이터 조회
export const getConstellationQRData = async (date: string): Promise<FunnelData | null> => {
  try {
    const constellationQRCollection = collection(funnelDb, 'constellation-qr');
    const docRef = doc(constellationQRCollection, date);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as FunnelData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('별자리연회 QR 데이터 조회 실패:', error);
    return null;
  }
};

// 화신 등록 시간을 로컬 스토리지에 저장
export const saveAvatarRegistrationTime = (registrationTime: Date): void => {
  try {
    const registrationData = {
      registrationTime: registrationTime.toISOString(),
      timestamp: Date.now()
    };
    localStorage.setItem('avatarRegistration', JSON.stringify(registrationData));
    console.log('화신 등록 시간 저장 완료:', registrationData);
  } catch (error) {
    console.error('화신 등록 시간 저장 실패:', error);
  }
};

// 로컬 스토리지에서 화신 등록 시간 조회
export const getAvatarRegistrationTime = (): { registrationTime: Date } | null => {
  try {
    const registrationData = localStorage.getItem('avatarRegistration');
    if (registrationData) {
      const parsed = JSON.parse(registrationData);
      return {
        registrationTime: new Date(parsed.registrationTime)
      };
    }
    return null;
  } catch (error) {
    console.error('화신 등록 시간 조회 실패:', error);
    return null;
  }
};

// 체험 시간 데이터를 Firestore에 저장 (동시성 문제 해결)
export const saveExperienceTimeData = async (
  avatarRegistrationTime: Date,
  messageSentTime: Date
): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = getTodayString();
      const experienceDuration = messageSentTime.getTime() - avatarRegistrationTime.getTime();
      const randomSuffix = Math.random().toString(36).substring(2, 5); // 예: "a9x"

      const experienceTimeCollection = collection(funnelDb, 'experience-times');
      // 타임스탬프를 기반으로 고유한 문서 ID 생성 (개인정보 없이)
      const uniqueId = `${targetDate}_${messageSentTime.getTime()}_${randomSuffix}`;
      const docRef = doc(experienceTimeCollection, uniqueId);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        // 이미 데이터가 있으면 중복 저장 방지
        if (docSnap.exists()) {
          console.log(`체험 시간 데이터가 이미 존재함: ${uniqueId}`);
          return;
        }
        
        const experienceData: ExperienceTimeData = {
          avatarRegistrationTime: avatarRegistrationTime as any, // Date를 Timestamp로 변환
          messageSentTime: messageSentTime as any, // Date를 Timestamp로 변환
          experienceDuration,
          date: targetDate,
          lastUpdated: serverTimestamp() as Timestamp
        };
        
        transaction.set(docRef, experienceData);
      });
      
      console.log('체험 시간 데이터 저장 완료:', { targetDate, experienceDuration });
      
      // 로컬 스토리지에서 화신 등록 데이터 삭제
      localStorage.removeItem('avatarRegistration');
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`체험 시간 데이터 저장 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('체험 시간 데이터 저장 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 특정 날짜의 체험 시간 데이터 조회
export const getExperienceTimeData = async (date: string): Promise<ExperienceTimeData[]> => {
  try {
    const experienceTimeCollection = collection(funnelDb, 'experience-times');
    const q = query(
      experienceTimeCollection,
      where('date', '==', date)
    );
    
    const querySnapshot = await getDocs(q);
    const experienceData: ExperienceTimeData[] = [];
    
    querySnapshot.forEach((doc) => {
      experienceData.push(doc.data() as ExperienceTimeData);
    });
    
    return experienceData;
  } catch (error) {
    console.error('체험 시간 데이터 조회 실패:', error);
    return [];
  }
};

// 배후성 선택 데이터 초기화
const initializeBackgroundSelectionData = (date: string): BackgroundSelectionData => {
  return {
    date,
    selections: {},
    totalSelections: 0,
    lastUpdated: serverTimestamp() as Timestamp
  };
};

// 배후성 선택 횟수 업데이트 (동시성 문제 해결)
export const updateBackgroundSelection = async (backgroundName: string, date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const backgroundSelectionCollection = collection(funnelDb, 'background-selections');
      const docRef = doc(backgroundSelectionCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 해당 배후성 선택 횟수 증가
          const currentData = docSnap.data() as BackgroundSelectionData;
          const currentSelections = currentData.selections || {};
          const currentCount = currentSelections[backgroundName] || 0;
          
          transaction.update(docRef, {
            [`selections.${backgroundName}`]: currentCount + 1,
            totalSelections: increment(1),
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeBackgroundSelectionData(targetDate);
          initialData.selections[backgroundName] = 1;
          initialData.totalSelections = 1;
          transaction.set(docRef, initialData);
        }
      });
      
      console.log(`배후성 선택 횟수 업데이트 성공: ${backgroundName} (${targetDate})`);
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`배후성 선택 횟수 업데이트 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('배후성 선택 횟수 업데이트 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 특정 날짜의 배후성 선택 데이터 조회
export const getBackgroundSelectionData = async (date: string): Promise<BackgroundSelectionData | null> => {
  try {
    const backgroundSelectionCollection = collection(funnelDb, 'background-selections');
    const docRef = doc(backgroundSelectionCollection, date);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as BackgroundSelectionData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('배후성 선택 데이터 조회 실패:', error);
    return null;
  }
};

// 퀴즈 진행도 데이터 초기화 (간단 버전)
const initializeQuizProgressData = (date: string): QuizProgressData => {
  const initialQuizData = {
    entries: 0,
    correct: 0,
    incorrect: 0
  };
  
  return {
    date,
    quizzes: {
      quiz1: { ...initialQuizData },
      quiz2: { ...initialQuizData },
      quiz3: { ...initialQuizData },
      quiz4: { ...initialQuizData },
      quiz5: { ...initialQuizData },
      quiz6: { ...initialQuizData }
    },
    lastUpdated: serverTimestamp() as Timestamp
  };
};

// 퀴즈 진행도 업데이트 (간단 버전 - 동시성 문제 해결)
export const updateQuizProgress = async (
  quizNumber: number,
  action: 'entries' | 'correct' | 'incorrect',
  date?: string
): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const quizProgressCollection = collection(funnelDb, 'quiz-progress');
      const docRef = doc(quizProgressCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 해당 퀴즈의 해당 액션 증가
          const currentData = docSnap.data() as QuizProgressData;
          const quizKey = `quiz${quizNumber}` as keyof typeof currentData.quizzes;
          const currentCount = currentData.quizzes[quizKey][action] || 0;
          
          transaction.update(docRef, {
            [`quizzes.${quizKey}.${action}`]: currentCount + 1,
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeQuizProgressData(targetDate);
          const quizKey = `quiz${quizNumber}` as keyof typeof initialData.quizzes;
          initialData.quizzes[quizKey][action] = 1;
          transaction.set(docRef, initialData);
        }
      });
      
      console.log(`퀴즈 ${quizNumber} ${action} 업데이트 성공: ${targetDate}`);
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`퀴즈 ${quizNumber} ${action} 업데이트 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error(`퀴즈 ${quizNumber} ${action} 업데이트 최종 실패:`, error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 특정 날짜의 퀴즈 진행도 데이터 조회
export const getQuizProgressData = async (date: string): Promise<QuizProgressData | null> => {
  try {
    const quizProgressCollection = collection(funnelDb, 'quiz-progress');
    const docRef = doc(quizProgressCollection, date);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as QuizProgressData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('퀴즈 진행도 데이터 조회 실패:', error);
    return null;
  }
};

// 체험 중단 포인트 데이터 초기화
const initializeDropoutData = (date: string): DropoutData => {
  return {
    date,
    registeredUsers: 0,
    messageSentUsers: 0,
    endingReachedUsers: 0,
    dropoutRate: 0,
    completionRate: 0,
    lastUpdated: serverTimestamp() as Timestamp
  };
};

// 등록한 유저 수 증가 (동시성 문제 해결)
export const incrementRegisteredUsers = async (date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const dropoutCollection = collection(funnelDb, 'dropout-data');
      const docRef = doc(dropoutCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 등록 유저 수 증가
          const currentData = docSnap.data() as DropoutData;
          const newRegisteredUsers = currentData.registeredUsers + 1;
          // 기존 데이터에 필드가 없을 수 있으므로 안전하게 처리
          const currentMessageSentUsers = currentData.messageSentUsers || 0;
          const currentEndingReachedUsers = currentData.endingReachedUsers || 0;
          const dropoutRate = newRegisteredUsers > 0 
            ? ((newRegisteredUsers - currentMessageSentUsers) / newRegisteredUsers * 100)
            : 0;
          const completionRate = newRegisteredUsers > 0 
            ? (currentEndingReachedUsers / newRegisteredUsers * 100)
            : 0;
          
          transaction.update(docRef, {
            registeredUsers: newRegisteredUsers,
            dropoutRate: dropoutRate,
            completionRate: completionRate,
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeDropoutData(targetDate);
          initialData.registeredUsers = 1;
          transaction.set(docRef, initialData);
        }
      });
      
      console.log(`등록 유저 수 증가 성공: ${targetDate}`);
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`등록 유저 수 증가 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('등록 유저 수 증가 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 메시지 전송한 유저 수 증가 (동시성 문제 해결)
export const incrementMessageSentUsers = async (date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const dropoutCollection = collection(funnelDb, 'dropout-data');
      const docRef = doc(dropoutCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 메시지 전송 유저 수 증가
          const currentData = docSnap.data() as DropoutData;
          const newMessageSentUsers = currentData.messageSentUsers + 1;
          // 기존 데이터에 필드가 없을 수 있으므로 안전하게 처리
          const currentEndingReachedUsers = currentData.endingReachedUsers || 0;
          const dropoutRate = currentData.registeredUsers > 0 
            ? ((currentData.registeredUsers - newMessageSentUsers) / currentData.registeredUsers * 100)
            : 0;
          const completionRate = currentData.registeredUsers > 0 
            ? (currentEndingReachedUsers / currentData.registeredUsers * 100)
            : 0;
          
          transaction.update(docRef, {
            messageSentUsers: newMessageSentUsers,
            dropoutRate: dropoutRate,
            completionRate: completionRate,
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeDropoutData(targetDate);
          initialData.messageSentUsers = 1;
          transaction.set(docRef, initialData);
        }
      });
      
      console.log(`메시지 전송 유저 수 증가 성공: ${targetDate}`);
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`메시지 전송 유저 수 증가 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('메시지 전송 유저 수 증가 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 엔딩까지 도달한 유저 수 증가 (동시성 문제 해결)
export const incrementEndingReachedUsers = async (date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const dropoutCollection = collection(funnelDb, 'dropout-data');
      const docRef = doc(dropoutCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 엔딩 도달 유저 수 증가
          const currentData = docSnap.data() as DropoutData;
          // 기존 데이터에 endingReachedUsers 필드가 없을 수 있으므로 안전하게 처리
          const currentEndingReachedUsers = currentData.endingReachedUsers || 0;
          const newEndingReachedUsers = currentEndingReachedUsers + 1;
          const dropoutRate = currentData.registeredUsers > 0 
            ? ((currentData.registeredUsers - currentData.messageSentUsers) / currentData.registeredUsers * 100)
            : 0;
          const completionRate = currentData.registeredUsers > 0 
            ? (newEndingReachedUsers / currentData.registeredUsers * 100)
            : 0;
          
          transaction.update(docRef, {
            endingReachedUsers: newEndingReachedUsers,
            dropoutRate: dropoutRate,
            completionRate: completionRate,
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeDropoutData(targetDate);
          initialData.endingReachedUsers = 1;
          transaction.set(docRef, initialData);
        }
      });
      
      console.log(`엔딩 도달 유저 수 증가 성공: ${targetDate}`);
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`엔딩 도달 유저 수 증가 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('엔딩 도달 유저 수 증가 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 특정 날짜의 체험 중단 포인트 데이터 조회
export const getDropoutData = async (date: string): Promise<DropoutData | null> => {
  try {
    const dropoutCollection = collection(funnelDb, 'dropout-data');
    const docRef = doc(dropoutCollection, date);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as DropoutData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('체험 중단 포인트 데이터 조회 실패:', error);
    return null;
  }
};

// 메시지 전송 횟수 데이터 초기화
const initializeMessageSendCountData = (date: string): MessageSendCountData => {
  return {
    date,
    totalMessageSends: 0,
    lastUpdated: serverTimestamp() as Timestamp
  };
};

// 메시지 전송 횟수 증가 (동시성 문제 해결)
export const incrementMessageSendCount = async (date?: string): Promise<void> => {
  const maxRetries = 5;
  let retryCount = 0;
  
  while (retryCount < maxRetries) {
    try {
      const targetDate = date || getTodayString();
      const messageSendCountCollection = collection(funnelDb, 'message-send-count');
      const docRef = doc(messageSendCountCollection, targetDate);
      
      // 트랜잭션을 사용하여 원자적 업데이트 보장
      await runTransaction(funnelDb, async (transaction) => {
        const docSnap = await transaction.get(docRef);
        
        if (docSnap.exists()) {
          // 문서가 존재하면 메시지 전송 횟수 증가
          const currentData = docSnap.data() as MessageSendCountData;
          const newTotalMessageSends = (currentData.totalMessageSends || 0) + 1;
          
          transaction.update(docRef, {
            totalMessageSends: newTotalMessageSends,
            lastUpdated: serverTimestamp()
          });
        } else {
          // 문서가 존재하지 않으면 새로 생성
          const initialData = initializeMessageSendCountData(targetDate);
          initialData.totalMessageSends = 1;
          transaction.set(docRef, initialData);
        }
      });
      
      console.log(`메시지 전송 횟수 증가 성공: ${targetDate}`);
      return; // 성공하면 함수 종료
      
    } catch (error) {
      retryCount++;
      console.error(`메시지 전송 횟수 증가 실패 (시도 ${retryCount}/${maxRetries}):`, error);
      
      if (retryCount >= maxRetries) {
        console.error('메시지 전송 횟수 증가 최종 실패:', error);
        throw error;
      }
      
      // 재시도 전 잠시 대기 (지수 백오프)
      await new Promise(resolve => setTimeout(resolve, Math.pow(2, retryCount) * 100));
    }
  }
};

// 특정 날짜의 메시지 전송 횟수 데이터 조회
export const getMessageSendCountData = async (date: string): Promise<MessageSendCountData | null> => {
  try {
    const messageSendCountCollection = collection(funnelDb, 'message-send-count');
    const docRef = doc(messageSendCountCollection, date);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data() as MessageSendCountData;
    } else {
      return null;
    }
  } catch (error) {
    console.error('메시지 전송 횟수 데이터 조회 실패:', error);
    return null;
  }
};


